// @ts-strict-ignore
import { IsThreeJsType } from '@3d-scene/common/util/is-threejs-type';
import { AxesHelper, Color, EdgesGeometry, Euler, LineBasicMaterial, LineSegments, Material, Matrix4, Mesh, MeshBasicMaterial, MeshPhongMaterial, Object3D, Quaternion, Vector3 } from 'three';
import { Rectangle3D } from '@3d-scene/model/rectangle-3d';
import { ArticleContainer } from '@3d-scene/model3d/article-container';
import { ConnectionAreaUsageVO, MetaElementUsageVO, MetaElementVO, TextureAreaUsageVO, TextureAreaVO } from '@shared/vo/meta-element.vo';
import { ArticleVO } from '@article/model/vo/article.vo';
import { MathUtils } from '@3d-scene/common/util/math-utils';
import { MOUSE_ENABLED, Object3DUtils } from '../common/util/object-3d-utils';
import { TextureUtils } from './util/texture-utils';
import { ArticleUsageUtils } from '@article/util/article-usage-utils';
import { DefinedMetaElementInfoPropertyDef } from '@project/constants/defined-meta-element-info-property-def';
import { SpriteMaterial } from '../material/sprite-material';
import { ArticleUtils } from '@article/util/article-utils';
import { LoadConnectionTextureProcessor } from '../processor/load-connection-texture.processor';
import { MeshUtils } from '@3d-scene/common/util/mesh-utils';
import { ConfiguratorScene } from '@3d-scene/scene/configurator-scene';
import { ProjectModel } from '@project/model/project.model';
import { ConnectionInfoUtils } from './util/connection-info-utils';
import { StringUtils } from '@shared/util/string-utils';
import { ProjectUtils } from '@project/components/project/util/project-utils';
import { GeometryUtils } from '../common/util/geometry-utils';
import { MetaElementUtils } from '@article/components/connections/connection-creation/meta-element.utils';

export class Connection {
  public static readonly TYPE_ARTICLE: string = 'ARTICLE';
  public static readonly TYPE_TEXT: string = 'TEXT';
  public static readonly TYPE_IMAGE: string = 'IMAGE';
  public static readonly TYPE_TEXT_OR_IMAGE_USAGE: string = 'TEXT_OR_IMAGE_USAGE';
  public static readonly TYPE_INTERACTION: string = 'INTERACTION';
  public static readonly TYPE_LIGHTING: string = 'LIGHTING';
  public static readonly TYPE_REFLECTION: string = 'REFLECTION';
  public static readonly TYPE_MONTAGE_POINT: string = 'MONTAGE_POINT';

  public static readonly DEFAULT_CONNECTION_OUTLINE_NAME: string = 'MeshOutline';

  public static readonly TEXT_TYPES: string[] = [Connection.TYPE_TEXT, Connection.TYPE_TEXT_OR_IMAGE_USAGE];
  public static readonly IMAGE_TYPES: string[] = [ Connection.TYPE_IMAGE, Connection.TYPE_TEXT_OR_IMAGE_USAGE ];

  public texturesEnabled: boolean = true;
  public bitmapRotation: number = SpriteMaterial.DEFAULT_BITMAP_ROTATION;
  public isTextureLoaded: boolean = false;
  public isImageLoaded: boolean = false;

  private _meshValuesInitialized: boolean;
  private _meshCentered: boolean;
  private _meshNormalized: boolean;
  private _meshPosition: Vector3;
  private _meshRotationDegree: Vector3;
  private _meshWidth: number;
  private _meshLength: number;
  private _meshRadius: number;
  private _meshOriginalWidth: number;
  private _meshOriginalLength: number;
  private _meshOriginalRadius: number;
  private _meshIsCircle: boolean;
  private _meshNormalizedVector: Vector3 = new Vector3(0, 0, 1);

  private _meshOutline: LineSegments;
  private axis: AxesHelper;
  private axisLength: number = 50; // could be made dynamic based on connection size

  private _id: string;
  private _mesh: Mesh;
  private _type: string;
  private readonly _uid: string = StringUtils.createUuid();
  private _metaElementVO: MetaElementVO;
  private _metaElementUsage: MetaElementUsageVO;
  private _isActive: boolean = true;
  private _selected: boolean = false;
  private _textureMaterialCreated: boolean = false;
  private _imageBitmap: HTMLImageElement = null;
  private _backgroundBitmap: HTMLImageElement = null;
  private _defaultMaterial: Material;
  private _defaultFillColor: number;
  private _defaultTransparent: boolean;

  constructor(
    private loadConnectionTextureProcessor: LoadConnectionTextureProcessor,
    private configuratorScene: ConfiguratorScene,
    private projectModel: ProjectModel,
    private _articleContainer: ArticleContainer,
    private updateConnectionAvailability: () => void,
    mesh: Mesh,
    type: string = null
  ) {
    this.setMesh(mesh);
    this._id = mesh?.name;
    this._type = type;
    this._defaultMaterial = mesh?.material as Material;
    this.removeChildrenByName(ArticleContainer.AXISHELPER_NAME);
    if (this.articleContainer) {
      this._metaElementVO = this.articleContainer.findMetaElementByName(this.id);
      // introduced to handle case when strip labeling article is placed at adapters and some article exists between adapters.
      // CreateLabelingHelper was not able to set correct size of texture connection because LabelingUtils could not find
      // nextMergedTextureConnection because at the time of search metaElementUsage was not set.
      this._metaElementUsage = this.articleContainer.findMetaElementUsageByName(this.id);

      if (this._mesh && this._mesh.geometry) {
        if (this.isAdminViewMaintenance()) {
          // update connection as such (and thus displaying axis) does not work for debug modus in configurator (Debug.active = true)
          // problem must exist for a long time and was not introduced by recent changes
          // thus currently only displaying axis and outline for maintaining connections in admin tool
          this.initializeMeshValues();
          this.forceUpdateMesh(
            this._meshPosition,
            this._meshRotationDegree,
            this._meshIsCircle ? this._meshRadius : this._meshWidth,
            this._meshLength
          );
          this.axis = MeshUtils.createAxesWithLabels(this.axisLength, this.configuratorScene.defaultFont, 8, 'U', 'V', 'N');
          this.axis.name = ArticleContainer.AXISHELPER_NAME;
          this._mesh.add(this.axis);
          this.axis.position.set(0, 0, 0);
          const axesRotationDegree: Vector3 = this.getMeshRotationDegree(false);
          const euler: Euler = new Euler();
          euler.set(MathUtils.toRadians(axesRotationDegree.x), MathUtils.toRadians(axesRotationDegree.y), MathUtils.toRadians(axesRotationDegree.z));
          this.axis.setRotationFromEuler(euler);
          this.axis.visible = this.selected;
          this.updateOutline();
        }
      }
    }
  }

  public isDyeable(): boolean {
    return this.isTextureArea
      && Connection.IMAGE_TYPES.includes(this._type)
      && MetaElementUtils.isDyeable(this._metaElementVO);
  }

  private isAdminViewMaintenance(): boolean {
    if (this.articleContainer.projectModel && this.articleContainer.projectModel.currentProject && this.articleContainer.projectModel.isAdminView) {
      return true;
    } else {
      return false;
    }
  }

  private updateOutline(): void {
    if (!this._mesh || !this._mesh.geometry || !this.isAdminViewMaintenance()) {
      return;
    }
    if (!this._meshOutline) {
      this.createOutline();
    }
    const material: LineBasicMaterial = this._meshOutline.material as LineBasicMaterial;
    if (this.selected) {
      material.color.set(ArticleContainer.SELECTION_COLOR);
    } else {
      material.color.set(ArticleContainer.CONTOUR_COLOR);
    }
  }

  private createOutline(): void {
    const geo: EdgesGeometry = new EdgesGeometry(this._mesh.geometry);
    const mat: LineBasicMaterial = new LineBasicMaterial({ color: (this.selected ? ArticleContainer.SELECTION_COLOR : ArticleContainer.CONTOUR_COLOR), linewidth: 3 });
    this._meshOutline = new LineSegments(geo, mat);
    this._meshOutline.name = Connection.DEFAULT_CONNECTION_OUTLINE_NAME;
    this._mesh.add(this._meshOutline);
  }

  /**
   * Identifier of the connection.
   * @return identifier
   *
   */
  public get id(): string {
    return this._id;
  }

  public get connectionName(): string {
    return this._id;
  }

  public set connectionName(connectionName: string) {
    this._id = connectionName;
    if (this._mesh) {
      this._mesh.name = connectionName;
    }
  }

  /**
   * Connection rectangle mesh.
   * @return mesh
   *
   */
  public get mesh(): Mesh {
    return this._mesh;
  }

  public setMesh(value: Mesh): void {
    this._mesh = value;
    this.clearMeshValues();
  }

  public get meshPosition(): Vector3 {
    this.initializeMeshValues();
    return this._meshPosition.clone();
  }

  public get meshRotationDegree(): Vector3 {
    this.initializeMeshValues();
    return this._meshRotationDegree.clone();
  }

  public get meshWidth(): number {
    this.initializeMeshValues();
    return this._meshWidth;
  }

  public get meshLength(): number {
    this.initializeMeshValues();
    return this._meshLength;
  }

  public get meshRadius(): number {
    this.initializeMeshValues();
    return this._meshRadius;
  }

  public get meshIsCircle(): boolean {
    this.initializeMeshValues();
    return this._meshIsCircle;
  }

  public get meshValuesInitialized(): boolean {
    this.initializeMeshValues();
    return this._meshValuesInitialized;
  }

  public get meshNormalizedVector(): Vector3 {
    return this._meshNormalizedVector.clone();
  }

  public get connectorNormal(): Vector3 {
    if (this._mesh) {
      const mesh: Mesh = this._mesh.clone() as Mesh;
      const geometry = mesh.geometry.clone();
      mesh.updateMatrix();
      geometry.applyMatrix4(mesh.matrix);
      return GeometryUtils.getFaces(geometry)[0].normal.clone();
    }
    return null;
  }

  private initializeMeshValues(): void {
    if (!this._meshValuesInitialized) {
      if (this._mesh && this._mesh.geometry) {
        this._mesh.geometry.computeBoundingBox();
        this._mesh.geometry.computeBoundingSphere();
        this._meshPosition = new Vector3(
          this._mesh.geometry.boundingSphere.center.x + this._mesh.position.x,
          this._mesh.geometry.boundingSphere.center.y + this._mesh.position.y,
          this._mesh.geometry.boundingSphere.center.z + this._mesh.position.z
        );
        this._meshIsCircle = GeometryUtils.getFaces(this._mesh.geometry).length > 2;
        if (!this._meshIsCircle) {
          const rectangle = this.localRectangle;
          this._meshWidth = this._meshOriginalWidth = rectangle ? rectangle.width : 0;
          this._meshLength = this._meshOriginalLength = rectangle ? rectangle.height : 0;
          this._meshRotationDegree = this.getMeshRotationDegree();
        } else {
          this._meshRadius = this._meshOriginalRadius = this._mesh.geometry.boundingSphere.radius;
          this._meshRotationDegree = this.getMeshRotationDegree();
        }
        this._meshValuesInitialized = true;
      } else {
        this.clearMeshValues();
      }
    }
  }

  public clearMeshValues(): void {
    this._meshValuesInitialized = false;
    this._meshCentered = false;
    this._meshNormalized = false;
    this._meshPosition = new Vector3(0, 0, 0);
    this._meshRotationDegree = new Vector3(0, 0, 0);
    this._meshWidth = this._meshOriginalWidth = 0;
    this._meshLength = this._meshOriginalLength = 0;
    this._meshRadius = this._meshOriginalRadius = 0;
    this._meshIsCircle = false;
  }

  public getMeshRotationDegree(global: boolean = true): Vector3 {
    const euler = this.getRotationEuler(global);
    return new Vector3(MathUtils.toDegrees(euler.x), MathUtils.toDegrees(euler.y), MathUtils.toDegrees(euler.z));
  }

  public getRotationEuler(global: boolean = true): Euler {
    const fromFace: Vector3[] = [new Vector3(0, 0, 0), new Vector3(1, 0, 0), new Vector3(0, 1, 0)];
    const toRectangle: Rectangle3D = global ? this.compositionRectangle : this.localRectangle;
    if (!toRectangle) {
      return new Euler();
    }
    const toFace: Vector3[] = toRectangle.toFace();
    const rotationMatrix: Matrix4 = MathUtils.getRotationMatrix(fromFace, toFace, false);
    const quaternion: Quaternion = new Quaternion();
    rotationMatrix.decompose(new Vector3(), quaternion, new Vector3());
    const euler: Euler = new Euler();
    euler.setFromQuaternion(quaternion);
    return euler;
  }

  private centerMesh(): void {
    this.initializeMeshValues();
    if (this._meshValuesInitialized) {
      if (!this._meshCentered) {
        this.mesh.geometry.center();
        if (this.axis) {
          this.axis.position.set(0, 0, 0);
        }
        this._meshCentered = true;
      }
    }
  }

  public normalizeMesh(): void {
    this.initializeMeshValues();
    if (this._meshValuesInitialized) {
      if (!this._meshNormalized) {
        this.centerMesh();
        this._mesh.position.set(0, 0, 0);
        this._mesh.updateMatrix();
        this._mesh.geometry.applyMatrix4(this._mesh.matrix);
        this._mesh.matrix.identity();
        this._mesh.scale.set(1, 1, 1);
        const euler: Euler = new Euler();
        euler.set(
          MathUtils.toRadians(-this._meshRotationDegree.x),
          MathUtils.toRadians(-this._meshRotationDegree.y),
          MathUtils.toRadians(-this._meshRotationDegree.z),
          'ZYX'
        );
        this._mesh.setRotationFromEuler(euler);
        this._mesh.updateMatrix();
        this._mesh.geometry.applyMatrix4(this._mesh.matrix);
        this._mesh.matrix.identity();
        this._mesh.rotation.set(0, 0, 0);
        if (this.axis) {
          this.axis.rotation.set(0, 0, 0);
          this.axis.scale.set(1, 1, 1);
        }
        this._meshNormalized = true;
      }
    }
  }

  public updateMesh(position: Vector3, rotationDegree: Vector3, widthOrRadius?: number, length?: number): void {
    this.initializeMeshValues();
    this.removeOutline();
    if (this._meshValuesInitialized) {
      if (widthOrRadius > 0 && (length == null || length > 0)) {
        if (
          !this._meshNormalized &&
          this._meshRotationDegree.equals(rotationDegree) &&
          ((!this._meshIsCircle && widthOrRadius === this._meshWidth && length === this._meshLength) ||
            (this._meshIsCircle && widthOrRadius === this._meshRadius))
        ) {
          if (!this._meshPosition.equals(position)) {
            this.centerMesh();
            this._meshPosition.copy(position);
            this._mesh.position.copy(position);
            this._mesh.geometry.computeBoundingBox();
            this._mesh.geometry.computeBoundingSphere();
          }
        } else {
          this.forceUpdateMesh(position, rotationDegree, widthOrRadius, length);
        }
      } else {
        this.forceUpdateMesh(position, rotationDegree, NaN, NaN);
      }
    }
    this.updateOutline();
  }

  private forceUpdateMesh(position: Vector3, rotationDegree: Vector3, widthOrRadius: number, length?: number): void {
    this.normalizeMesh();
    this._meshPosition.copy(position);
    this._mesh.position.copy(position);
    this._meshRotationDegree.copy(rotationDegree);
    const euler: Euler = new Euler();
    euler.set(MathUtils.toRadians(rotationDegree.x), MathUtils.toRadians(rotationDegree.y), MathUtils.toRadians(rotationDegree.z));
    this._mesh.setRotationFromEuler(euler);
    if (!this._meshIsCircle) {
      if (!isNaN(widthOrRadius) && !isNaN(length)) {
        this._mesh.scale.set(length / this._meshOriginalLength, widthOrRadius / this._meshOriginalWidth, 1);
        if (this.axis) {
          this.axis.scale.set(this._meshOriginalLength / length, this._meshOriginalWidth / widthOrRadius, 1);
        }
        this._meshWidth = widthOrRadius;
        this._meshLength = length;
      }
    } else {
      if (!isNaN(widthOrRadius)) {
        this._mesh.scale.set(
          MathUtils.roundNumberValue(widthOrRadius / this._meshOriginalRadius, 5),
          MathUtils.roundNumberValue(widthOrRadius / this._meshOriginalRadius, 5),
          1
        );
        if (this.axis) {
          this.axis.scale.set(
            MathUtils.roundNumberValue(this._meshOriginalRadius / widthOrRadius, 5),
            MathUtils.roundNumberValue(this._meshOriginalRadius / widthOrRadius, 5),
            1
          );
        }
        this._meshRadius = widthOrRadius;
      }
    }
    this._mesh.geometry.computeBoundingBox();
    this._mesh.geometry.computeBoundingSphere();
  }

  public get localRectangle(): Rectangle3D {
    return MathUtils.calculateBoundingUVRectangle(this._mesh.geometry, this._mesh.scale);
  }

  /**
   * Returns cloned rectangle with 'world' coordinates.
   * @return rectangle
   *
   */
  public get globalRectangle(): Rectangle3D {
    // Calculate bounding rectangle by geometry and set rectangle points to 'world'
    const boundingRectangle: Rectangle3D = this.localRectangle;
    if (boundingRectangle) {
      this.mesh.updateMatrixWorld(true);
      this.mesh.localToWorld(boundingRectangle.point1);
      this.mesh.localToWorld(boundingRectangle.point2);
      this.mesh.localToWorld(boundingRectangle.point3);
      this.mesh.localToWorld(boundingRectangle.point4);
      return boundingRectangle;
    }
    return null;
  }

  /**
   * Returns cloned rectangle to which parent article container's coordinates were applied
   * @return rectangle
   *
   */
  public get compositionRectangle(): Rectangle3D {
    // Set rectangle points to 'world'
    let rectangle: Rectangle3D = null;
    const localRectangle: Rectangle3D = this.localRectangle;
    if (localRectangle && this._articleContainer) {
      this._articleContainer.updateMatrix();
      const transformationMatrix: Matrix4 = this._articleContainer.matrix;
      const point1: Vector3 = localRectangle.point1.clone().applyMatrix4(transformationMatrix);
      const point2: Vector3 = localRectangle.point2.clone().applyMatrix4(transformationMatrix);
      const point3: Vector3 = localRectangle.point3.clone().applyMatrix4(transformationMatrix);
      const point4: Vector3 = localRectangle.point4.clone().applyMatrix4(transformationMatrix);
      rectangle = Rectangle3D.fromArray([point1, point2, point3, point4]);
    }
    return rectangle;
  }

  /**
   * Type of the connection
   * @return type
   */
  public get type(): string {
    return this._type;
  }

  /**
   * Type of the connection
   * @param type new type
   *
   */
  public set type(type: string) {
    this._type = type;
  }

  public get uid(): string {
    return this._uid;
  }

  public get articleContainer(): ArticleContainer {
    return this._articleContainer;
  }

  public get metaElementVO(): MetaElementVO {
    return this._metaElementVO;
  }

  public set metaElementVO(metaElement: MetaElementVO) {
    this._metaElementVO = metaElement;
  }

  public get metaElementUsage(): MetaElementUsageVO {
    return this._metaElementUsage;
  }

  public set metaElementUsage(usage: MetaElementUsageVO) {
    this._metaElementUsage = usage;
  }

  public get isActive(): boolean {
    return this._isActive;
  }

  public set isActive(value: boolean) {
    if (this._isActive !== value) {
      this._isActive = value;
      this.updateMeshVisibility(this.mesh);
    }
  }

  public get selected(): boolean {
    return this._selected;
  }

  public set selected(value: boolean) {
    if (this._selected !== value) {
      this._selected = value;
      this.refreshSelection();
    }
  }

  public get article(): ArticleVO {
    if (!this.articleContainer || !this.articleContainer.article) {
      return null;
    }
    return this.articleContainer.article;
  }

  private updateMeshVisibility(mesh: Mesh): void {
    if (mesh) {
      if (
        this.isSpriteMaterialCreationEnabled() &&
        this._metaElementVO instanceof TextureAreaVO &&
        ConnectionInfoUtils.isTextConnectionType(this._metaElementVO.textureType)
      ) {
        this.updateMeshTextureMaterial();
      }
      mesh.visible = this._isActive;
      (mesh.material as Material).visible = this._isActive;
    }
  }

  private updateMeshTextureMaterial(): void {
    if (this._isActive) {
      this.createMeshTextureMaterial();
      this.isTextureLoaded = false;
      this.loadAndApplyTextureToConnection();
    } else {
      this.disposeMaterial();
    }
  }

  private loadAndApplyTextureToConnection(): void {
    if (this.isTextureLoaded || !(this.metaElementUsage instanceof TextureAreaUsageVO)) {
      return;
    }
    this.loadConnectionTextureProcessor.loadAndApplyImagesToConnections([this]);
  }

  private disposeMaterial(): void {
    this._imageBitmap = null;
    this._backgroundBitmap = null;
    this._defaultMaterial = null;
    this.createMeshTextureMaterial();
    this._textureMaterialCreated = false;
  }

  public updateMouseEnabled(value: boolean): void {
    this.updateMouseEnabledForMesh(this.mesh, value);
  }

  private updateMouseEnabledForMesh(mesh: Mesh, value: boolean): void {
    if (mesh) {
      mesh[MOUSE_ENABLED] = value;
    }
  }

  public isMetaInfoShown(): boolean {
    if (!this.mesh) {
      return false;
    }
    const parentArticleContainer: ArticleContainer = this.articleContainer;
    const metaInfoShown: boolean = parentArticleContainer && parentArticleContainer.projectModel &&
    ConnectionInfoUtils.isConnectionDisplayed(this._metaElementVO);

    return metaInfoShown;
  }

  public refresh(): void {
    if (!this.mesh) {
      return;
    }
    if (this.metaElementUsage instanceof ConnectionAreaUsageVO) {
      this.updateConnectionAvailability();
    } else {
      const enabled: boolean = this.isMetaInfoShown() && this.mesh.material && (this.mesh.material as Material).visible;
      this.updateMouseEnabled(enabled);
    }
  }

  private refreshSelection(): void {
    if (!this.mesh || !this.mesh.material) {
      return;
    }
    if (!this.projectModel.isCompositeAdminView) {
      this.refreshMaterialOnSelection();
      if (this.axis) {
        this.axis.visible = this._selected;
      }
    } else if (this.axis) {
      this.axis.visible = false;
    }
    this.updateOutline();
  }

  private refreshMaterialOnSelection() {
    if (this.selected) {
      if (this._defaultMaterial instanceof SpriteMaterial) {
        const sprite: SpriteMaterial = this.mesh.material as SpriteMaterial;
        sprite.selected = true;
        sprite.fillColor = 0x8ee3ff;
        sprite.setTransparent(false);
      } else {
        this.mesh.material = new MeshBasicMaterial({ color: 0x00bfff, side: this._defaultMaterial.side});
      }
    } else {
      if (this._defaultMaterial instanceof SpriteMaterial) {
        this.updateSpriteMaterialOnDeselect();
      } else {
        if (this.mesh.material) {
          (<Material>this.mesh.material).dispose();
        }
        this.mesh.material = this._defaultMaterial;
      }
    }
  }

  private updateSpriteMaterialOnDeselect(): void {
    const sprite: SpriteMaterial = this.mesh.material as SpriteMaterial;
    sprite.selected = false;
    sprite.fillColor = this._defaultFillColor;
    if (this.isDyeable()) {
      const articleUsageId = this._metaElementUsage.identity.articleUsageId;
      const articleUsage = ProjectUtils.findArticleUsageInProjectById(this.projectModel.currentProject, articleUsageId);
      const imageExists = articleUsage.definedMetaElementInfoProperties.some((property) => {
        return this.connectionName === property.identity.metaElementName &&
          property.property.name === DefinedMetaElementInfoPropertyDef.CUSTOM_IMAGE_HASH
      });
      sprite.setTransparent(imageExists);
    } else {
      sprite.setTransparent(this._defaultTransparent);
    }
  }

  public cloneMesh3DResources(): void {
    if (this._mesh) {
      this._mesh.geometry = this._mesh.geometry.clone();
      this._mesh.material = (this._mesh.material as Material).clone();
    }
  }

  public toString(): string {
    return this._id;
  }

  public get imageBitmap(): HTMLImageElement {
    return this._imageBitmap;
  }

  public set imageBitmap(image: HTMLImageElement) {
    this._imageBitmap = image;
  }

  public get backgroundBitmap(): HTMLImageElement {
    return this._backgroundBitmap;
  }

  public set backgroundBitmap(value: HTMLImageElement) {
    this._backgroundBitmap = value;
  }

  public refreshTexture(): void {
    // Connection is already "disposed". Can happen e.g. during replace of article
    if (!this.mesh.material) {
      return;
    }
    if (this._metaElementUsage instanceof TextureAreaUsageVO && this.isSpriteMaterialCreationEnabled() && this.canRefreshTexture()) {
      if (this._textureMaterialCreated && this.mesh.material instanceof SpriteMaterial) {
        this.updateMaterial();
      } else {
        this.createMeshTextureMaterial();
      }
      this.loadAndApplyTextureToConnection();
    }
  }

  public isTextureArea(): boolean {
    return this.metaElementVO instanceof TextureAreaVO;
  }

  private updateMaterial(): void {
    if (this.mesh.material instanceof SpriteMaterial) {
      this.mesh.material.backgroundBitmapData = this.backgroundBitmap;
      this.mesh.material.imageBitmapData = this.imageBitmap;
      this.mesh.material.updateMaterial(this.isDyeable() && this.isImageLoaded);
    }
  }

  private canRefreshTexture(): boolean {
    if (!this.texturesEnabled || !this._isActive) {
      return false;
    }
    const customImageHash: string = ArticleUsageUtils.getDefinedMetaElementInfoPropertyStringValue(
      this.articleContainer.articleUsage,
      this.connectionName,
      DefinedMetaElementInfoPropertyDef.CUSTOM_IMAGE_HASH
    );
    return !(customImageHash && !this.imageBitmap);
  }

  private isSpriteMaterialCreationEnabled(): boolean {
    if (!this.articleContainer) {
      return true;
    }
    return !this.articleContainer.projectModel.isAdminView || this.articleContainer.projectModel.isCompositeAdminView;
  }

  public createMeshTextureMaterial(): void {
    const isDefaultRotation: boolean = ArticleUtils.isDynamicLabelingArticle(this.article) && !ArticleUtils.isCoverLabelStripArticle(this.article);
    const transparent = this._mesh?.material ? (this._mesh.material as Material).transparent : true;
    const hexColor = (this._mesh?.material as MeshPhongMaterial).color.getHex() ?? 0xffffff;
    const material: SpriteMaterial = TextureUtils.createTextMaterial(
      this.mesh,
      transparent,
      hexColor,
      isDefaultRotation,
      this.bitmapRotation
    );
    const oldMaterial: Material = this.mesh.material as Material;
    material.visible = oldMaterial.visible;
    this.mesh.material = material;
    this.updateMaterial();
    if (oldMaterial) {
      oldMaterial.dispose();
    }
    this.updateDefaultMaterialParameters(this.mesh.material);
    this._textureMaterialCreated = true;
    this._defaultMaterial = this.mesh.material;
  }

  private updateDefaultMaterialParameters(defaultMaterial: Material): void {
    this._defaultMaterial = defaultMaterial;
    if (this._defaultMaterial instanceof SpriteMaterial) {
      this._defaultFillColor = this._defaultMaterial.fillColor;
      this._defaultTransparent = this._defaultMaterial.transparent;
    }
  }

  public getDefaultFillColor(): number {
    if (this._defaultMaterial instanceof SpriteMaterial) {
      this._defaultFillColor = this._defaultMaterial.fillColor;
      this._defaultTransparent = this._defaultMaterial.transparent;
    } else if (IsThreeJsType.meshPhongMaterial(this._defaultMaterial)) {
      this._defaultFillColor = this._defaultMaterial.color.getHex();
    } else if (IsThreeJsType.meshBasicMaterial(this._defaultMaterial)) {
      this._defaultFillColor = this._defaultMaterial.color.getHex();
    }
    return this._defaultFillColor;
  }

  public setDefaultFillColor(hexColor: number): void {
    if (this._defaultMaterial instanceof SpriteMaterial) {
      const defaultMaterial: SpriteMaterial = this._defaultMaterial as SpriteMaterial;
      defaultMaterial.fillColor = hexColor;
    } else if (IsThreeJsType.meshPhongMaterial(this._defaultMaterial)) {
      const defaultMaterial: MeshPhongMaterial = this._defaultMaterial;
      defaultMaterial.color = new Color(hexColor);
    } else if (IsThreeJsType.meshBasicMaterial(this._defaultMaterial)) {
      const defaultMaterial: MeshBasicMaterial = this._defaultMaterial;
      defaultMaterial.color = new Color(hexColor);
    }
    if (this._defaultMaterial) {
      this._defaultMaterial.needsUpdate = true;
    }
  }

  private removeOutline(): void {
    if (!this._meshOutline) {
      return;
    }
    this.removeChildrenByName(Connection.DEFAULT_CONNECTION_OUTLINE_NAME);
    Object3DUtils.destroy(this._meshOutline);
    this._meshOutline = null;
  }

  public resetTexture(): void {
    this.imageBitmap = null;
    this.backgroundBitmap = null;
    this.isTextureLoaded = false;
    this.isImageLoaded = false;
  }

  public getColorAsHexString(): string {
    return '#' + ('000000' + this.getDefaultFillColor().toString(16)).slice(-6);
  }

  /**
   * this._mesh.remove(Object3D) seems to be bugged ? idk
   */
  private removeChildrenByName(objectName: string): void {
    if (!this._mesh) {
      return;
    }
    this._mesh.children = this._mesh.children.filter((value, index, arr) => value.name !== objectName);
  }

  public setDefaultOpacity(opacity: number): void {
    if (this._defaultMaterial) {
      this._defaultTransparent = opacity !== 1;
      this._defaultMaterial.transparent = this._defaultTransparent;
      this._defaultMaterial.opacity = opacity;
      this._defaultMaterial.needsUpdate = true;
    }
  }

  public getDefaultOpacity(): number {
    if (this._defaultMaterial) {
      return this._defaultMaterial.opacity;
    }
  }

  public getDefaultMaterial(): Material {
    return this._defaultMaterial;
  }

  public setDefaultMaterial(material: Material): void {
    this._defaultMaterial = material;
    if (!this.selected) {
      this.mesh.material = this._defaultMaterial;
    }
  }

  private cloneConnectionMesh(): Mesh {
    // AxesHelper is not cloned correctly, because constructor of AxesHelper is:
    // constructor( size?: number );
    // instead of expected signature by three,js cloning method:
    // constructor( geometry?: Geometry | BufferGeometry,	material?: Material | Material[], ...? )
    const clonedMesh: Mesh = MeshUtils.cloneMesh(this._mesh);
    if (clonedMesh) {
      // fixing incorrectly cloned AxesHelper:
      const clonedAxis: AxesHelper = <AxesHelper>clonedMesh.getObjectByName(ArticleContainer.AXISHELPER_NAME);
      if (clonedAxis && this.axis) {
        if (clonedAxis.geometry) {
          clonedAxis.geometry.dispose();
        }
        if (clonedAxis.material) {
          (<Material>clonedAxis.material).dispose();
        }
        clonedAxis.geometry = this.axis.geometry.clone();
        clonedAxis.material = this.axis.material.clone();
      }
    }
    return clonedMesh;
  }

  public clone(): Connection {
    const clone: Connection = new Connection(
      this.loadConnectionTextureProcessor,
      this.configuratorScene,
      this.projectModel,
      this.articleContainer,
      this.updateConnectionAvailability,
      null,
      this._type);
    clone.copy(this);
    return clone;
  }

  public copy(connection: Connection): Connection {
    this.texturesEnabled = connection.texturesEnabled;
    this.bitmapRotation = connection.bitmapRotation;
    this.isTextureLoaded = connection.isTextureLoaded;
    this.isImageLoaded = connection.isImageLoaded;

    this._meshValuesInitialized = connection._meshValuesInitialized;
    this._meshCentered = connection._meshCentered;
    this._meshNormalized = connection._meshNormalized;
    this._meshPosition = new Vector3().copy(connection._meshPosition);
    this._meshRotationDegree = new Vector3().copy(connection._meshRotationDegree);
    this._meshWidth = connection._meshWidth;
    this._meshLength = connection._meshLength;
    this._meshRadius = connection._meshRadius;
    this._meshOriginalWidth = connection._meshOriginalWidth;
    this._meshOriginalLength = connection._meshOriginalLength;
    this._meshOriginalRadius = connection._meshOriginalRadius;
    this._meshIsCircle = connection._meshIsCircle;
    this._meshNormalizedVector = new Vector3().copy(connection._meshNormalizedVector);
    this.axisLength = connection.axisLength;

    this._id = connection._id;

    this._type = connection._type;
    this._articleContainer = connection._articleContainer;
    this._metaElementVO = connection._metaElementVO;
    this._metaElementUsage = connection._metaElementUsage;
    this._isActive = connection._isActive;
    this._selected = connection._selected;
    this._textureMaterialCreated = connection._textureMaterialCreated;
    this._imageBitmap = connection._imageBitmap;
    this._backgroundBitmap = connection._backgroundBitmap;
    if (this._defaultMaterial) {
      this._defaultMaterial.dispose();
    }
    this._defaultMaterial = connection._defaultMaterial ? connection._defaultMaterial.clone() : connection._defaultMaterial;
    this._defaultFillColor = connection._defaultFillColor;
    this._defaultTransparent = connection._defaultTransparent;

    const parent: Object3D = this._mesh ? this._mesh.parent : null;
    if (this._mesh) {
      if (parent) {
        parent.remove(this._mesh);
      }
      Object3DUtils.destroy(this._mesh);
    }
    this._mesh = connection.cloneConnectionMesh();
    // only in case mesh was included in scene before, should cloned mesh be added again (thus replacing the removed mesh in scene):
    if (parent && this._mesh) {
      parent.add(this._mesh);
    }
    if (this._mesh && connection._meshOutline) {
      this._meshOutline = <LineSegments>this._mesh.getObjectByName(Connection.DEFAULT_CONNECTION_OUTLINE_NAME);
    } else {
      this._meshOutline = null;
    }
    if (this._mesh && connection.axis) {
      this.axis = <AxesHelper>this._mesh.getObjectByName(ArticleContainer.AXISHELPER_NAME);
    } else {
      this.axis = null;
    }

    return this;
  }
}
